import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { OrderModel } from '@/types/model/Order'
import { CourierIntegration } from './CourierIntegration'
import { OrderNoteSelect } from '../cells/OrderNoteSelect'
import { OrderSourceSelect } from '../cells/OrderSourceSelect'
import { DeliveryLocationTypeSelect } from '../cells/DeliveryLocationTypeSelect'

interface OrderStatusCardProps {
    order: OrderModel.Order
    loading: boolean
    onStatusUpdate: (status: string) => void
    refreshOrder: () => void
}

/** An order relation the API expanded, as an object or a one-item array; `null` when it's only an id. */
const expandedOf = <T extends object>(value: string | null | T | T[]): T | null =>
    Array.isArray(value) ? (value[0] ?? null) : value && typeof value === 'object' ? value : null

export const OrderStatusCard = ({ order, loading, onStatusUpdate, refreshOrder }: OrderStatusCardProps) => {
    const status = order.status
    const showCourier = status === 'ready_to_ship' || !!(order.courier_info?.consignment_id)

    const expandedNote = Array.isArray(order.order_note_id) ? order.order_note_id[0] : null
    const noteId = typeof order.order_note_id === 'string' ? order.order_note_id : expandedNote?._id || null

    const expandedSource = Array.isArray(order.order_source_id) ? order.order_source_id[0] : null
    const sourceId = typeof order.order_source_id === 'string' ? order.order_source_id : expandedSource?._id || null

    const expandedLocation = Array.isArray(order.delivery_location_type_id) ? order.delivery_location_type_id[0] : null
    const locationTypeId = typeof order.delivery_location_type_id === 'string'
        ? order.delivery_location_type_id
        : (expandedLocation as any)?._id || (order.delivery_location_type_id as any)?._id || null

    return (
        <Card>
            <CardHeader>
                <CardTitle>Order management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-1.5">
                    <Label htmlFor="order-status" className="text-sm text-ink">Status</Label>
                    <Select value={status} onValueChange={onStatusUpdate} disabled={loading}>
                        <SelectTrigger id="order-status" className="w-full">
                            <SelectValue placeholder="Update status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="ready_to_ship">Ready to ship</SelectItem>
                            <SelectItem value="dispatched">Dispatched</SelectItem>
                            <SelectItem value="on_the_way">On the way</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1.5">
                    <p className="text-sm text-ink">Order note</p>
                    <div onClick={(e) => e.stopPropagation()}>
                        <OrderNoteSelect
                            orderId={order._id}
                            currentNoteId={noteId}
                            currentLabel={expandedOf(order.order_note_id)?.content}
                            onUpdate={refreshOrder}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <p className="text-sm text-ink">Source</p>
                    <div onClick={(e) => e.stopPropagation()}>
                        <OrderSourceSelect
                            orderId={order._id}
                            currentSourceId={sourceId}
                            currentLabel={expandedOf(order.order_source_id)?.name}
                            onUpdate={refreshOrder}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <p className="text-sm text-ink">Delivery area</p>
                    <div onClick={(e) => e.stopPropagation()}>
                        <DeliveryLocationTypeSelect
                            orderId={order._id}
                            currentLocationTypeId={locationTypeId}
                            currentLabel={expandedOf(order.delivery_location_type_id)?.name}
                            onUpdate={refreshOrder}
                        />
                    </div>
                </div>
            </CardContent>

            {showCourier && (
                <div className="border-t border-border-subtle p-4">
                    <CourierIntegration order={order} refreshOrder={refreshOrder} />
                </div>
            )}
        </Card>
    )
}
